import { Section, SectionHeader } from '../components/ui/Section'
import { Card, CardContent } from '../components/ui/Card'

type ProjectStatus = 'live-experiment' | 'beta' | 'alpha' | 'development' | 'research' | 'retired'
type ProjectCategory = 'Healthcare' | 'AI' | 'Finance' | 'Kind Capitalism' | 'Research'

interface Project {
  title: string
  description: string
  status: ProjectStatus
  category: ProjectCategory
  url?: string
}

const statusColors: Record<ProjectStatus, string> = {
  'live-experiment': 'bg-amber-100 text-amber-800',
  beta: 'bg-blue-100 text-blue-700',
  alpha: 'bg-purple-100 text-purple-700',
  development: 'bg-yellow-100 text-yellow-700',
  research: 'bg-slate-100 text-slate-700',
  retired: 'bg-stone-200 text-stone-700',
}

const statusLabels: Record<ProjectStatus, string> = {
  'live-experiment': 'Live · Experimental',
  beta: 'Beta',
  alpha: 'Alpha',
  development: 'In Development',
  research: 'Research',
  retired: 'Retired',
}

// Projects data - easily editable
const projects: Project[] = [
  {
    title: 'HAL',
    description: 'An experimental, non-clinical conversational grounding tool — a reflective place to think out loud. Not therapy, diagnosis, or a crisis service; benefit and clinical safety are not established. In an emergency, call or text 988 (US) or local emergency services.',
    status: 'live-experiment',
    category: 'AI',
    url: 'https://sum1namedhal.com',
  },
  {
    title: 'STAY. Pamphlet',
    description: 'Crisis survival in your pocket. Breathing, grounding, movement, waiting, darkness — one exercise per page, no login, works offline.',
    status: 'live-experiment',
    category: 'Healthcare',
    url: 'https://staypamphlet.com',
  },
  {
    title: 'Hello World, Gently',
    description: 'Interactive grief workbook, daily noticing book, and supporter guide. For those grieving and those walking beside them.',
    status: 'live-experiment',
    category: 'Healthcare',
    url: 'https://helloworldgently.com',
  },
  {
    title: 'Existential Math',
    description: 'A translational cognitive research program drawing on cognitive defusion (ACT), decentering, and predictive processing to build memorable, interactive reflective tools. Experimental and non-clinical.',
    status: 'beta',
    category: 'Research',
    url: 'https://existentialmath.com',
  },
  {
    title: 'HAAPL Foundation',
    description: 'Human-Agent Alignment and Practice Lab. The public hub connecting three doors: somatic distress tolerance (Bell), conversational presence (Loop), and neuroscience research (Orchard).',
    status: 'beta',
    category: 'AI',
    url: 'https://haapl.com',
  },
  {
    title: 'PerspectiveScope',
    description: 'An interactive cognitive bias and decentering instrument. Teaches users to recognize when intellectual sophistication, complex rationalizations, and "empathy" become defensive identity armor.',
    status: 'alpha',
    category: 'Healthcare',
  },
  {
    title: 'Ambient Documentation',
    description: 'AI-powered documentation that captures encounters without disrupting care.',
    status: 'development',
    category: 'Healthcare',
    url: 'https://talkudoc.com',
  },
  {
    title: 'EHR Workflow Tools',
    description: 'Clinician-centered workflow optimization for electronic health records.',
    status: 'alpha',
    category: 'Healthcare',
  },
  {
    title: 'Identity Adaptive Model (IAM)',
    description: 'A provisional, fitted grammar for how identity constructs may relate to strain — a reflective prompt, not a treatment model. In a 2026 prompt experiment, a 600-word iAM prompt supplement showed no meaningful difference from control on automated scoring; human scoring has not yet been done.',
    status: 'research',
    category: 'Research',
  },
  {
    title: 'Kind Capitalism',
    description: 'Provisional work on paid participation, responsible incentives, ethical licensing, structural accountability, and measurable public benefit.',
    status: 'research',
    category: 'Kind Capitalism',
    url: '/applications/kind-capitalism',
  },
  {
    title: 'SEC Tools Archive',
    description: 'Retired filing-alert experiment covering 8-K, Form 4, and broader SEC filings. Code and data archive preserved; subscriptions and alerts stopped.',
    status: 'retired',
    category: 'Finance',
  },
]

export function ProjectsPage() {
  return (
    <>
      {/* Hero */}
      <section className="bg-gradient-to-b from-slate-50 to-white py-16 md:py-24">
        <div className="container-wide">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 text-balance">
            Projects & Tools
          </h1>
          <p className="mt-8 text-xl text-slate-600 leading-relaxed max-w-2xl">
            Products, experiments, and research across healthcare, governed
            human–agent collaboration, and responsible economic design.
          </p>
        </div>
      </section>

      {/* Projects Grid */}
      <Section className="bg-white pt-0">
        <SectionHeader
          title="Current Projects"
          subtitle="Everything here is experimental: live prototypes, early builds, and research. None of it is a clinical service or a finished product."
        />

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <Card
              key={project.title}
              href={project.url}
              hover={!!project.url}
              className="p-6"
            >
              <CardContent className="p-0">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <span className={`inline-block px-2 py-1 text-xs font-medium rounded ${statusColors[project.status]}`}>
                    {statusLabels[project.status]}
                  </span>
                  <span className="text-xs text-slate-400">{project.category}</span>
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">{project.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{project.description}</p>
                {project.url && (
                  <div className="mt-4 text-primary-600 text-sm font-medium flex items-center gap-1">
                    Visit site
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Legend */}
        <div className="mt-12 p-6 bg-slate-50 rounded-xl">
          <h3 className="font-semibold text-slate-900 mb-4">Project Status Legend</h3>
          <div className="flex flex-wrap gap-4">
            {Object.entries(statusLabels).map(([status, label]) => (
              <div key={status} className="flex items-center gap-2">
                <span className={`px-2 py-1 text-xs font-medium rounded ${statusColors[status as ProjectStatus]}`}>
                  {label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </Section>
    </>
  )
}
